/**
 * WorkflowMetadataPanel - Panel for editing workflow-level metadata
 *
 * NOTE: Only display messages (welcomeMessage, errorMessage) are in metadata.
 * ALL prompts are stored in nodes via `systemMessage` field - click on a node to edit its prompt.
 */

import { memo, useMemo } from 'react';
import { useWorkflowMetadata } from '../../../../stores/workflow';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '../WorkflowEditor/defaultWorkflow';

interface WorkflowMetadataPanelProps {
  isOpen: boolean;
  onClose: () => void;
  languageCode?: SupportedLanguage;
}

export const WorkflowMetadataPanel = memo(({ isOpen, onClose, languageCode = 'vi' }: WorkflowMetadataPanelProps) => {
  const { t } = useTranslation('admin');
  const { workflowMetadata, setWelcomeMessage, setErrorMessage } = useWorkflowMetadata();

  // Short hint placeholders (not full prompts)
  const placeholderHints = useMemo(
    () =>
      languageCode === 'en'
        ? {
            welcomeMessage: 'e.g., Hello! How can I help you today?',
            errorMessage: 'e.g., Sorry, an error occurred...'
          }
        : {
            welcomeMessage: 'Ví dụ: Xin chào! Tôi có thể giúp gì cho bạn?',
            errorMessage: 'Ví dụ: Xin lỗi, đã có lỗi xảy ra...'
          },
    [languageCode]
  );

  if (!isOpen) return null;

  return (
    <div className='h-full w-96 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
      {/* Header */}
      <div className='sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-border-dark dark:bg-dark-background-secondary'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900 dark:text-dark-text'>
            {t('workflow.metadataPanel.title', 'Workflow Settings')}
          </h3>
          <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>
            {t('workflow.metadataPanel.subtitle', 'Configure display messages')}
          </p>
        </div>
        <button
          onClick={onClose}
          className='rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-background-hover dark:hover:text-dark-text-secondary'
        >
          <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className='p-4'>
        {/* Info about prompts location */}
        <div className='mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20'>
          <p className='text-xs text-blue-700 dark:text-blue-300'>
            {t(
              'workflow.metadataPanel.promptsNote',
              'System prompts are configured by clicking on workflow nodes (orchestrator and agent nodes).'
            )}
          </p>
        </div>

        {/* Welcome Message */}
        <div className='mb-4'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
            {t('workflow.metadataPanel.welcomeMessage', 'Welcome Message')}
          </label>
          <p className='mb-2 text-xs text-gray-500 dark:text-dark-text-secondary'>
            {t('workflow.metadataPanel.welcomeMessageHelp', 'Greeting shown when a new chat starts')}
          </p>
          <textarea
            value={workflowMetadata.welcomeMessage || ''}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            rows={3}
            placeholder={placeholderHints.welcomeMessage}
            className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
          />
        </div>

        {/* Error Message */}
        <div className='mb-4'>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
            {t('workflow.metadataPanel.errorMessage', 'Error Message')}
          </label>
          <p className='mb-2 text-xs text-gray-500 dark:text-dark-text-secondary'>
            {t('workflow.metadataPanel.errorMessageHelp', 'Shown when an error occurs during processing')}
          </p>
          <textarea
            value={workflowMetadata.errorMessage || ''}
            onChange={(e) => setErrorMessage(e.target.value)}
            rows={3}
            placeholder={placeholderHints.errorMessage}
            className='w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
          />
        </div>
      </div>
    </div>
  );
});

WorkflowMetadataPanel.displayName = 'WorkflowMetadataPanel';

export default WorkflowMetadataPanel;
